import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';
import { fetchAllArtists } from '../services/artistsService';
import { CACHE_KEYS } from '../services/kioskCache';
import useKioskData from '../hooks/useKioskData';
import OfflineBanner from '../components/OfflineBanner';
import loadingAnimation from '../assets/loading.json';
import usePageMeta from '../hooks/usePageMeta';
import { artistAvatar } from '../lib/imageProxy';
import BlurImage from '../components/BlurImage';
import './ArtistsPage.css';

const pickCardImage = (artist) =>
  artist.profile_image_url
    || artist.cover_image_url
    || artist.showcase_image_1_url
    || null;

// When the artist has priority (creatorAboutFirst), their own name is what
// gets shown and sorted on — mirrors the hero headline swap on the artist's
// own page.
const cardDisplayName = (artist) =>
  (artist.creator_about_first && artist.creator_name)
    ? artist.creator_name
    : (artist.name || artist.artist_name || '');

const ArtistsPage = () => {
  const { t } = useTranslation('artist');

  const { data, loading, error, fromCache } = useKioskData(CACHE_KEYS.artists, fetchAllArtists);

  usePageMeta({
    title: t('list.metaTitle'),
    description: t('list.metaDescription'),
  });

  const visibleArtists = useMemo(
    () => [...(data ?? [])].sort((a, b) => cardDisplayName(a).localeCompare(cardDisplayName(b))),
    [data]
  );

  if (loading) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <Lottie
          animationData={loadingAnimation}
          loop
          style={{ width: 200, height: 200, margin: '0 auto' }}
        />
      </div>
    );
  }

  return (
    <article className="artists-page">
      <OfflineBanner show={fromCache} />
      <header className="artists-page-header">
        <h1 className="artists-page-title">{t('list.title')}</h1>
      </header>

      {error ? (
        <div className="artists-page-empty">
          <p>{error}</p>
        </div>
      ) : visibleArtists.length === 0 ? (
        <div className="artists-page-empty">
          <p>{t('list.empty')}</p>
        </div>
      ) : (
        <div className="artists-page-grid">
          {visibleArtists.map((artist, index) => {
            const cardImage = pickCardImage(artist);
            const displayName = cardDisplayName(artist);
            const target = artist.slug || artist.id || artist.artist_id;
            return (
              <Link
                key={artist.id || artist.slug}
                to={`/artist/${target}`}
                className="artist-card"
              >
                <figure className="artist-card-figure">
                  {cardImage ? (
                    <BlurImage
                      src={artistAvatar(cardImage)}
                      alt={t('list.cardImageAlt', { name: displayName })}
                      loading={index < 4 ? 'eager' : 'lazy'}
                      objectFit="cover"
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                    />
                  ) : (
                    <div className="artist-card-placeholder" aria-hidden="true">
                      {(displayName || '?').charAt(0)}
                    </div>
                  )}
                </figure>
                <figcaption className="artist-card-caption">
                  <span className="artist-card-name">{displayName}</span>
                  {(artist.nationality_flag || artist.nationality) && (
                    <span
                      className="artist-card-flag"
                      title={artist.nationality_name || artist.nationality || ''}
                      aria-label={artist.nationality_name || artist.nationality || ''}
                    >
                      {artist.nationality_flag || artist.nationality}
                    </span>
                  )}
                </figcaption>
              </Link>
            );
          })}
        </div>
      )}
    </article>
  );
};

export default ArtistsPage;
